import React, { useEffect, useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TimeSelector } from '../components/dashboard/TimeSelector';
import { DashboardCard } from '../components/dashboard/DashboardCard';
import { CardBrandsTable } from '../components/dashboard/CardBrandsTable';
import { CardBrandsChart } from '../components/dashboard/CardBrandsChart';
import { KpiCard } from '../components/dashboard/KpiCard';
import { TabView } from '../components/dashboard/TabView';
import { GeoPaymentMap } from '../components/dashboard/GeoPaymentMap';
import { CardErrorCodes } from '../components/dashboard/CardErrorCodes';
import { CompanySelector, Company } from '../components/dashboard/CompanySelector';
import { CreditCardIcon, ArrowUpRightIcon, ArrowDownRightIcon, BarChart3Icon, DollarSignIcon, UsersIcon, ClockIcon, PercentIcon, CheckCircleIcon, GlobeIcon, BuildingIcon, FilterIcon, RotateCcwIcon, ClockIcon as PendingIcon, AlertCircleIcon, ReceiptIcon, RefreshCcwIcon, X } from 'lucide-react';
import { generatePaymentVolumeData, generateTransactionCountData, generateAverageTransactionData, generatePaymentMethodsData, generateConversionData, generateAttemptsData, generateGeoPaymentData, generateCompanies, filterDataByCompanies, generateRefundData, generatePendingRefundsData, generateRefundReasonsData, generatePreviousPeriodData, generatePreviousTransactionCountData, generatePreviousRefundData } from '../components/dashboard/DataGenerator';
export function Overview() {
  const [timeRange, setTimeRange] = useState('7d');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [paymentVolumeData, setPaymentVolumeData] = useState([]);
  const [previousPaymentVolumeData, setPreviousPaymentVolumeData] = useState([]);
  const [transactionCountData, setTransactionCountData] = useState([]);
  const [previousTransactionCountData, setPreviousTransactionCountData] = useState([]);
  const [averageTransactionData, setAverageTransactionData] = useState([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [attemptsData, setAttemptsData] = useState([]);
  const [geoPaymentData, setGeoPaymentData] = useState([]);
  const [refundData, setRefundData] = useState([]);
  const [previousRefundData, setPreviousRefundData] = useState([]);
  const [pendingRefundsData, setPendingRefundsData] = useState([]);
  const [refundReasonsData, setRefundReasonsData] = useState([]);
  // Load companies on component mount
  useEffect(() => {
    const companiesData = generateCompanies();
    setCompanies(companiesData);
    // Default to all companies selected
    setSelectedCompanies(companiesData);
  }, []);
  // Update data when time range or selected companies change
  useEffect(() => {
    // Get raw data
    const rawPaymentVolumeData = generatePaymentVolumeData(timeRange);
    const rawPreviousPaymentVolumeData = generatePreviousPeriodData(rawPaymentVolumeData, timeRange);
    const rawTransactionCountData = generateTransactionCountData(timeRange);
    const rawPreviousTransactionCountData = generatePreviousTransactionCountData(rawTransactionCountData, timeRange);
    const rawAverageTransactionData = generateAverageTransactionData(timeRange);
    const rawPaymentMethodsData = generatePaymentMethodsData();
    const rawConversionData = generateConversionData();
    const rawAttemptsData = generateAttemptsData();
    const rawGeoPaymentData = generateGeoPaymentData();
    const rawRefundData = generateRefundData(timeRange);
    const rawPreviousRefundData = generatePreviousRefundData(rawRefundData, timeRange);
    const rawPendingRefundsData = generatePendingRefundsData();
    const rawRefundReasonsData = generateRefundReasonsData();
    // Filter data by selected companies if needed
    const selectedCompanyIds = selectedCompanies.map(company => company.id);
    const isAllCompanies = selectedCompanyIds.length === companies.length;
    if (isAllCompanies || selectedCompanyIds.length === 0) {
      // Use all data
      setPaymentVolumeData(rawPaymentVolumeData);
      setPreviousPaymentVolumeData(rawPreviousPaymentVolumeData);
      setTransactionCountData(rawTransactionCountData);
      setPreviousTransactionCountData(rawPreviousTransactionCountData);
      setAverageTransactionData(rawAverageTransactionData);
      setPaymentMethodsData(rawPaymentMethodsData);
      setConversionData(rawConversionData);
      setAttemptsData(rawAttemptsData);
      setGeoPaymentData(rawGeoPaymentData);
      setRefundData(rawRefundData);
      setPreviousRefundData(rawPreviousRefundData);
      setPendingRefundsData(rawPendingRefundsData);
      setRefundReasonsData(rawRefundReasonsData);
    } else {
      // Filter data based on selected companies
      setPaymentVolumeData(filterDataByCompanies(rawPaymentVolumeData, selectedCompanyIds));
      setPreviousPaymentVolumeData(filterDataByCompanies(rawPreviousPaymentVolumeData, selectedCompanyIds));
      setTransactionCountData(filterDataByCompanies(rawTransactionCountData, selectedCompanyIds));
      setPreviousTransactionCountData(filterDataByCompanies(rawPreviousTransactionCountData, selectedCompanyIds));
      setAverageTransactionData(filterDataByCompanies(rawAverageTransactionData, selectedCompanyIds));
      setPaymentMethodsData(rawPaymentMethodsData); // Keep the same proportions
      setConversionData(rawConversionData); // Keep the same proportions
      setAttemptsData(filterDataByCompanies(rawAttemptsData, selectedCompanyIds));
      setGeoPaymentData(filterDataByCompanies(rawGeoPaymentData, selectedCompanyIds));
      setRefundData(filterDataByCompanies(rawRefundData, selectedCompanyIds));
      setPreviousRefundData(filterDataByCompanies(rawPreviousRefundData, selectedCompanyIds));
      setPendingRefundsData(filterDataByCompanies(rawPendingRefundsData, selectedCompanyIds));
      setRefundReasonsData(rawRefundReasonsData); // Keep the same proportions
    }
  }, [timeRange, selectedCompanies, companies]);
  // Calculate total payment volume
  const totalVolume = paymentVolumeData.reduce((sum, item) => sum + item.amount, 0);
  // Calculate total transactions
  const totalTransactions = transactionCountData.reduce((sum, item) => sum + item.count, 0);
  // Calculate average transaction
  const avgTransaction = totalVolume / totalTransactions;
  // Calculate conversion rate
  const conversionRate = conversionData.length > 2 ? conversionData[2].value / conversionData[0].value * 100 : 0;
  // Calculate percentage of transactions by single attempt
  const singleAttemptRate = attemptsData.length > 0 ? attemptsData[0].count / attemptsData.reduce((sum, item) => sum + item.count, 0) * 100 : 0;
  // Calculate average number of attempts
  const avgAttempts = attemptsData.length > 0 ? attemptsData.reduce((sum, item, index) => {
    const attemptNumber = index === 3 ? 4 : index + 1; // 4+ is counted as 4
    return sum + item.count * attemptNumber;
  }, 0) / attemptsData.reduce((sum, item) => sum + item.count, 0) : 0;
  // Calculate refund metrics
  const totalRefundAmount = refundData.reduce((sum, item) => sum + item.amount, 0);
  const totalRefundCount = refundData.reduce((sum, item) => sum + item.count, 0);
  const refundPercentage = totalVolume > 0 ? totalRefundAmount / totalVolume * 100 : 0;
  // Format dates based on time range
  const formatDate = (date, timeRange) => {
    if (timeRange === 'today') {
      // For today's data, format as hour
      if (typeof date === 'string' && date.includes('T')) {
        const dateObj = new Date(date);
        return dateObj.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
      // If we have an hour field, use that
      return date;
    } else if (timeRange === '7d' || timeRange === '30d') {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } else {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    }
  };
  // Get the title for the time period based on selected range
  const getTimeRangeTitle = () => {
    switch (timeRange) {
      case 'today':
        return 'Today (hourly)';
      case '7d':
        return 'Last 7 days';
      case '30d':
        return 'Last 30 days';
      case 'year':
        return 'This year';
      case 'all':
        return 'All time';
      default:
        return 'Selected period';
    }
  };
  // Get the x-axis key for the data
  const getXAxisKey = () => {
    return timeRange === 'today' ? 'hour' : 'date';
  };
  // Table view for payment volume data
  const PaymentVolumeTable = () => <div className="overflow-x-auto" data-id="element-4348">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4349">
        <thead className="bg-gray-50" data-id="element-4350">
          <tr data-id="element-4351">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4352">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4353">
              Volume
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4354">
          {paymentVolumeData.map((row, index) => <tr key={index} data-id="element-4355">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4356">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4357">
                Rs. {row.amount.toLocaleString('en-LK')}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for transaction count data
  const TransactionCountTable = () => <div className="overflow-x-auto" data-id="element-4358">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4359">
        <thead className="bg-gray-50" data-id="element-4360">
          <tr data-id="element-4361">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4362">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4363">
              Transactions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4364">
          {transactionCountData.map((row, index) => <tr key={index} data-id="element-4365">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4366">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4367">
                {row.count.toLocaleString()}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for payment methods data
  const PaymentMethodsTable = () => <div className="overflow-x-auto" data-id="element-4368">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4369">
        <thead className="bg-gray-50" data-id="element-4370">
          <tr data-id="element-4371">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4372">
              Payment Method
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4373">
              Percentage
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4374">
          {paymentMethodsData.map((row, index) => <tr key={index} data-id="element-4375">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4376">
                {row.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4377">
                {row.value}%
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for refund data
  const RefundDataTable = () => <div className="overflow-x-auto" data-id="element-4378">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4379">
        <thead className="bg-gray-50" data-id="element-4380">
          <tr data-id="element-4381">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4382">
              Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4383">
              Refund Amount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4384">
              Count
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4385">
          {refundData.map((row, index) => <tr key={index} data-id="element-4386">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4387">
                {formatDate(row.date, timeRange)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4388">
                Rs. {row.amount.toLocaleString('en-LK')}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4389">
                {row.count}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Table view for geographic payment data
  const GeoPaymentTable = () => <div className="overflow-x-auto" data-id="element-4390">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-4391">
        <thead className="bg-gray-50" data-id="element-4392">
          <tr data-id="element-4393">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4394">
              Country
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4395">
              Transactions
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4396">
              Volume
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-4397">
          {geoPaymentData.map((row, index) => <tr key={index} data-id="element-4398">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4399">
                {row.country}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4400">
                {row.transactions.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4401">
                Rs. {row.amount.toLocaleString('en-LK')}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
  // Determine if we're showing aggregated data
  const isAggregated = selectedCompanies.length > 1 && selectedCompanies.length < companies.length;
  return <div className="space-y-6" data-id="element-4402">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-4403">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-4404">
          Dashboard Overview
        </h1>
        <div className="flex flex-col gap-3 w-full md:w-auto" data-id="element-4405">
          {/* Controls row - Time Range Selector first, then Company Selector */}
          <div className="flex flex-wrap items-center gap-3 justify-end" data-id="element-4406">
            {/* Time Range Selector */}
            <TimeSelector value={timeRange} onChange={setTimeRange} data-id="element-4407" />
            {/* Company Selector */}
            {companies.length > 0 && <CompanySelector companies={companies} selectedCompanies={selectedCompanies} onChange={setSelectedCompanies} data-id="element-4408" />}
          </div>
          {/* Selected company tags in a separate row */}
          {selectedCompanies.length > 1 && selectedCompanies.length < companies.length && <div className="flex flex-wrap gap-2 mt-2 justify-end" data-id="element-4409">
                {selectedCompanies.slice(0, 3).map(company => <div key={company.id} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full" data-id="element-4410">
                    {company.name}
                    <button onClick={() => {
              setSelectedCompanies(selectedCompanies.filter(c => c.id !== company.id));
            }} className="ml-1 text-blue-500 hover:text-blue-700" data-id="element-4411">
                      <X size={12} data-id="element-4412" />
                    </button>
                  </div>)}
                {selectedCompanies.length > 3 && <div className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-4413">
                    +{selectedCompanies.length - 3} more
                  </div>}
              </div>}
        </div>
      </div>
      {/* Aggregation indicator */}
      {isAggregated && <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start" data-id="element-4414">
          <FilterIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-4415" />
          <div data-id="element-4416">
            <p className="text-sm text-blue-800" data-id="element-4417">
              <span className="font-medium" data-id="element-4418">Aggregated view:</span> Showing
              combined data for {selectedCompanies.length} companies.
            </p>
            <div className="mt-1 flex flex-wrap gap-1" data-id="element-4419">
              {selectedCompanies.map(company => <span key={company.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-4420">
                  {company.name}
                </span>)}
            </div>
          </div>
        </div>}
      {/* First row: Only 4 KPIs in a horizontal row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-4421">
        <KpiCard title="Total Volume" value={`Rs. ${(totalVolume / 1000).toFixed(1)}k`} icon={<DollarSignIcon size={18} data-id="element-4423" />} trend={{
        value: '8.2%',
        isPositive: true
      }} color="blue" data-id="element-4422" />
        <KpiCard title="Transactions" value={totalTransactions.toLocaleString()} icon={<BarChart3Icon size={18} data-id="element-4425" />} trend={{
        value: '5.7%',
        isPositive: true
      }} color="green" data-id="element-4424" />
        <KpiCard title="Avg Transaction" value={`Rs. ${avgTransaction.toFixed(2)}`} icon={<ArrowUpRightIcon size={18} data-id="element-4427" />} trend={{
        value: '2.3%',
        isPositive: true
      }} color="purple" data-id="element-4426" />
        <KpiCard title="Conversion" value={`${conversionRate.toFixed(1)}%`} icon={<CreditCardIcon size={18} data-id="element-4429" />} trend={{
        value: '1.4%',
        isPositive: false
      }} color="orange" data-id="element-4428" />
      </div>
      {/* Second row: 3-column chart and 1-column stacked KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4430">
        {/* Payment Volume Chart (3 columns) */}
        <div className="lg:col-span-3 flex flex-col h-full" data-id="element-4431">
          <DashboardCard title="Payment Volume" subtitle={getTimeRangeTitle()} className="h-full flex-grow" data-id="element-4432">
            <TabView tabs={[{
            label: 'Chart',
            content: <div className="h-80" data-id="element-4434">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-4435">
                        <LineChart data={[...paymentVolumeData, ...previousPaymentVolumeData]} data-id="element-4436">
                          <CartesianGrid strokeDasharray="3 3" data-id="element-4437" />
                          <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-4438" />
                          <YAxis tickFormatter={value => `Rs. ${value / 1000}k`} data-id="element-4439" />
                          <Tooltip formatter={value => [`Rs. ${value.toLocaleString('en-LK')}`, 'Volume']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-4440" />
                          <Legend data-id="element-4441" />
                          <Line type="monotone" dataKey="amount" stroke="#6E9FD9" strokeWidth={2} name="Current Period" connectNulls dot={{
                    r: 2
                  }} activeDot={{
                    r: 6
                  }} data={paymentVolumeData} data-id="element-4442" />
                          <Line type="monotone" dataKey="amount" stroke="#6E9FD9" strokeWidth={1.5} strokeDasharray="5 5" name="Previous Period" connectNulls dot={false} data={previousPaymentVolumeData} data-id="element-4443" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
          }, {
            label: 'Data',
            content: <PaymentVolumeTable data-id="element-4444" />
          }]} data-id="element-4433" />
          </DashboardCard>
        </div>
        {/* Stacked KPIs (1 column) */}
        <div className="flex flex-col h-full" data-id="element-4445">
          <div className="space-y-4 flex-grow flex flex-col justify-between" data-id="element-4446">
            <KpiCard title="Daily Average" value={`Rs. ${(totalVolume / paymentVolumeData.length / 1000).toFixed(1)}k`} icon={<DollarSignIcon size={18} data-id="element-4448" />} trend={{
            value: '4.5%',
            isPositive: true
          }} color="blue" data-id="element-4447" />
            <KpiCard title="Monthly Growth" value="12.7%" icon={<ArrowUpRightIcon size={18} data-id="element-4450" />} trend={{
            value: '3.2%',
            isPositive: true
          }} color="green" data-id="element-4449" />
            <KpiCard title="YoY Growth" value="24.3%" icon={<BarChart3Icon size={18} data-id="element-4452" />} trend={{
            value: '5.8%',
            isPositive: true
          }} color="purple" data-id="element-4451" />
            <KpiCard title="Transaction Growth" value="18.5%" icon={<ArrowUpRightIcon size={18} data-id="element-4454" />} trend={{
            value: '2.7%',
            isPositive: true
          }} color="orange" data-id="element-4453" />
          </div>
        </div>
      </div>
      {/* Third row: Two 2-column charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4455">
        {/* Transaction Count Chart (2 columns) */}
        <DashboardCard title="Number of Transactions" subtitle={getTimeRangeTitle()} data-id="element-4456">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-64" data-id="element-4458">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-4459">
                      <LineChart data={[...transactionCountData, ...previousTransactionCountData]} data-id="element-4460">
                        <CartesianGrid strokeDasharray="3 3" data-id="element-4461" />
                        <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-4462" />
                        <YAxis data-id="element-4463" />
                        <Tooltip formatter={value => [value.toLocaleString(), 'Transactions']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-4464" />
                        <Legend data-id="element-4465" />
                        <Line type="monotone" dataKey="count" stroke="#9C7CB6" strokeWidth={2} name="Current Period" connectNulls dot={{
                  r: 2
                }} activeDot={{
                  r: 6
                }} data={transactionCountData} data-id="element-4466" />
                        <Line type="monotone" dataKey="count" stroke="#9C7CB6" strokeWidth={1.5} strokeDasharray="5 5" name="Previous Period" connectNulls dot={false} data={previousTransactionCountData} data-id="element-4467" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <TransactionCountTable data-id="element-4468" />
        }]} data-id="element-4457" />
        </DashboardCard>
        {/* Average Transaction Chart (2 columns) */}
        <DashboardCard title="Average Transaction" subtitle={`Last ${timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : 'year'}`} data-id="element-4469">
          <div className="h-64" data-id="element-4470">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4471">
              <BarChart data={averageTransactionData} data-id="element-4472">
                <CartesianGrid strokeDasharray="3 3" data-id="element-4473" />
                <XAxis dataKey="date" tickFormatter={date => formatDate(date, timeRange)} data-id="element-4474" />
                <YAxis tickFormatter={value => `Rs. ${value}`} data-id="element-4475" />
                <Tooltip formatter={value => [`Rs. ${value.toLocaleString('en-LK')}`, 'Average']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-4476" />
                <Bar dataKey="amount" fill="#7FC29B" data-id="element-4477" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </DashboardCard>
      </div>
      {/* Fourth row: Card Brands chart and Success Rate */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4478">
        {/* Card Brands Chart (3 columns) */}
        <div className="lg:col-span-3 flex flex-col" data-id="element-4479">
          <DashboardCard title="Card Brands" subtitle="Transaction Volume" className="h-full flex-grow" data-id="element-4480">
            <TabView tabs={[{
            label: 'Chart',
            content: <div className="h-80" data-id="element-4482">
                      <CardBrandsChart data-id="element-4483" />
                    </div>
          }, {
            label: 'Data',
            content: <CardBrandsTable data-id="element-4484" />
          }]} data-id="element-4481" />
          </DashboardCard>
        </div>
        {/* Card Success Rate (1 column) */}
        <div className="flex flex-col" data-id="element-4485">
          <DashboardCard title="Card Success Rate" subtitle="By Brand" className="h-full flex-grow" data-id="element-4486">
            <div className="space-y-3 p-2" data-id="element-4487">
              <div className="flex justify-between items-center" data-id="element-4488">
                <div className="flex items-center" data-id="element-4489">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/visa.svg" alt="Visa" className="h-6 w-10 mr-2" data-id="element-4490" />
                  <span data-id="element-4491">Visa</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-4492">98.2%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-4493">
                <div className="flex items-center" data-id="element-4494">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/mastercard.svg" alt="MasterCard" className="h-6 w-10 mr-2" data-id="element-4495" />
                  <span data-id="element-4496">MasterCard</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-4497">97.5%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-4498">
                <div className="flex items-center" data-id="element-4499">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/amex.svg" alt="Amex" className="h-6 w-10 mr-2" data-id="element-4500" />
                  <span data-id="element-4501">Amex</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-4502">95.8%</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-4503">
                <div className="flex items-center" data-id="element-4504">
                  <img src="https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/master/flat/discover.svg" alt="Discover" className="h-6 w-10 mr-2" data-id="element-4505" />
                  <span data-id="element-4506">Discover</span>
                </div>
                <span className="font-medium text-green-600" data-id="element-4507">96.3%</span>
              </div>
            </div>
            <CardErrorCodes timeRange={timeRange} data-id="element-4508" />
          </DashboardCard>
        </div>
      </div>
      {/* New fifth row: Card Volume KPIs in horizontal layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-4509">
        <KpiCard title="Visa Volume" value="Rs. 112.5k" icon={<CreditCardIcon size={18} data-id="element-4511" />} color="blue" data-id="element-4510" />
        <KpiCard title="MC Volume" value="Rs. 87.3k" icon={<CreditCardIcon size={18} data-id="element-4513" />} color="orange" data-id="element-4512" />
        <KpiCard title="Amex Volume" value="Rs. 42.8k" icon={<CreditCardIcon size={18} data-id="element-4515" />} color="green" data-id="element-4514" />
        <KpiCard title="Discover Volume" value="Rs. 21.4k" icon={<CreditCardIcon size={18} data-id="element-4517" />} color="purple" data-id="element-4516" />
      </div>
      {/* Fifth row: Two 2-column charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4518">
        {/* Payment Methods Chart (2 columns) */}
        <DashboardCard title="Payment Methods" subtitle="Transaction Count" data-id="element-4519">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-80" data-id="element-4521">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-4522">
                      <PieChart data-id="element-4523">
                        <Pie data={paymentMethodsData} cx="50%" cy="50%" labelLine={false} outerRadius={90} fill="#8884d8" dataKey="value" label={({
                  name,
                  percent
                }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-4524">
                          {paymentMethodsData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4525" />)}
                        </Pie>
                        <Tooltip formatter={value => [`${value}%`, 'Percentage']} data-id="element-4526" />
                        <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{
                  fontSize: '12px'
                }} data-id="element-4527" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <PaymentMethodsTable data-id="element-4528" />
        }]} data-id="element-4520" />
        </DashboardCard>
        {/* Payment Conversion Chart (2 columns) */}
        <DashboardCard title="Payment Conversion" subtitle="Success Rate" data-id="element-4529">
          <div className="h-64" data-id="element-4530">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4531">
              <BarChart data={conversionData} data-id="element-4532">
                <CartesianGrid strokeDasharray="3 3" data-id="element-4533" />
                <XAxis dataKey="name" data-id="element-4534" />
                <YAxis data-id="element-4535" />
                <Tooltip formatter={value => [value.toLocaleString(), 'Transactions']} data-id="element-4536" />
                <Bar dataKey="value" fill="#3498db" data-id="element-4537">
                  {conversionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4538" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-center text-sm text-gray-600" data-id="element-4539">
            Conversion rate: {conversionRate.toFixed(1)}%
          </div>
        </DashboardCard>
      </div>
      {/* Sixth row: One 2-column chart and 4 vertically stacked KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4540">
        {/* Attempts Per Payment Chart (2 columns) */}
        <div className="flex flex-col h-full" data-id="element-4541">
          <DashboardCard title="Attempts Per Payment" subtitle="Before Success" className="h-full flex-grow" data-id="element-4542">
            <div className="h-80" data-id="element-4543">
              <ResponsiveContainer width="100%" height="100%" data-id="element-4544">
                <BarChart data={attemptsData} data-id="element-4545">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-4546" />
                  <XAxis dataKey="attempts" data-id="element-4547" />
                  <YAxis data-id="element-4548" />
                  <Tooltip formatter={value => [value.toLocaleString(), 'Payments']} data-id="element-4549" />
                  <Bar dataKey="count" fill="#5DAFBA" data-id="element-4550" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-center text-sm text-gray-600" data-id="element-4551">
              Average attempts: {avgAttempts.toFixed(1)}
            </div>
          </DashboardCard>
        </div>
        {/* 4 Vertically stacked KPIs */}
        <div className="flex flex-col h-full" data-id="element-4552">
          <div className="space-y-4 flex-grow flex flex-col justify-between" data-id="element-4553">
            <KpiCard title="First Attempt" value={`${singleAttemptRate.toFixed(1)}%`} icon={<CheckCircleIcon size={18} data-id="element-4555" />} trend={{
            value: '3.1%',
            isPositive: true
          }} color="green" data-id="element-4554" />
            <KpiCard title="Avg Attempts" value={avgAttempts.toFixed(1)} icon={<ClockIcon size={18} data-id="element-4557" />} trend={{
            value: '0.2',
            isPositive: false
          }} color="blue" data-id="element-4556" />
            <KpiCard title="Customers" value="1,243" icon={<UsersIcon size={18} data-id="element-4559" />} trend={{
            value: '12.5%',
            isPositive: true
          }} color="purple" data-id="element-4558" />
            <KpiCard title="Chargeback" value="0.3%" icon={<PercentIcon size={18} data-id="element-4561" />} trend={{
            value: '0.1%',
            isPositive: true
          }} color="orange" data-id="element-4560" />
          </div>
        </div>
      </div>
      {/* New Refunds Row: Header */}
      <div className="mt-8 mb-4" data-id="element-4562">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center" data-id="element-4563">
          <RotateCcwIcon size={20} className="mr-2 text-red-500" data-id="element-4564" />
          Refund Analysis
        </h2>
      </div>
      {/* Refunds KPIs row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-4565">
        <KpiCard title="Total Refunds" value={`Rs. ${(totalRefundAmount / 1000).toFixed(1)}k`} icon={<RotateCcwIcon size={18} data-id="element-4567" />} trend={{
        value: '2.1%',
        isPositive: false
      }} color="red" data-id="element-4566" />
        <KpiCard title="Refund Count" value={totalRefundCount} icon={<ReceiptIcon size={18} data-id="element-4569" />} trend={{
        value: '1.5%',
        isPositive: false
      }} color="red" data-id="element-4568" />
        <KpiCard title="Refund Rate" value={`${refundPercentage.toFixed(1)}%`} icon={<PercentIcon size={18} data-id="element-4571" />} trend={{
        value: '0.2%',
        isPositive: true
      }} color="blue" data-id="element-4570" />
        <KpiCard title="Avg Refund" value={`Rs. ${(totalRefundAmount / totalRefundCount).toFixed(2)}`} icon={<DollarSignIcon size={18} data-id="element-4573" />} trend={{
        value: '3.8%',
        isPositive: false
      }} color="orange" data-id="element-4572" />
      </div>
      {/* Refunds charts row - Modified to remove Pending Refunds */}
      <div className="grid grid-cols-1 gap-6" data-id="element-4574">
        {/* Refund Amounts Chart (full width) */}
        <DashboardCard title="Refund Volume" subtitle={getTimeRangeTitle()} icon={<RotateCcwIcon size={18} className="text-red-500 mr-2" data-id="element-4576" />} className="h-full flex-grow" data-id="element-4575">
          <TabView tabs={[{
          label: 'Chart',
          content: <div className="h-80" data-id="element-4578">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-4579">
                      <LineChart data={[...refundData, ...previousRefundData]} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-4580">
                        <CartesianGrid strokeDasharray="3 3" data-id="element-4581" />
                        <XAxis dataKey={getXAxisKey()} tickFormatter={value => formatDate(value, timeRange)} data-id="element-4582" />
                        <YAxis tickFormatter={value => `Rs. ${value}`} data-id="element-4583" />
                        <Tooltip formatter={value => [`Rs. ${value.toLocaleString('en-LK')}`, 'Refund Amount']} labelFormatter={label => formatDate(label, timeRange)} data-id="element-4584" />
                        <Legend data-id="element-4585" />
                        <Line type="monotone" dataKey="amount" name="Current Period" stroke="#ef4444" strokeWidth={2} activeDot={{
                  r: 6
                }} connectNulls dot={{
                  r: 2
                }} data={refundData} data-id="element-4586" />
                        <Line type="monotone" dataKey="amount" name="Previous Period" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="5 5" connectNulls dot={false} data={previousRefundData} data-id="element-4587" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
        }, {
          label: 'Data',
          content: <RefundDataTable data-id="element-4588" />
        }]} data-id="element-4577" />
        </DashboardCard>
      </div>
      {/* Refund Reasons */}
      <div data-id="element-4589">
        <DashboardCard title="Refund Reasons" subtitle="Distribution by Category" icon={<RefreshCcwIcon size={18} className="text-blue-500 mr-2" data-id="element-4591" />} data-id="element-4590">
          <div className="h-80" data-id="element-4592">
            <ResponsiveContainer width="100%" height="100%" data-id="element-4593">
              <PieChart data-id="element-4594">
                <Pie data={refundReasonsData} cx="50%" cy="50%" labelLine={false} outerRadius={90} dataKey="count" nameKey="reason" label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-4595">
                  {refundReasonsData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4596" />)}
                </Pie>
                <Tooltip formatter={value => [value, 'Count']} data-id="element-4597" />
                <Legend data-id="element-4598" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </DashboardCard>
      </div>
      {/* Seventh row: Geographic payment map */}
      <div data-id="element-4599">
        <DashboardCard title="Payment Geography" subtitle="Transaction Origin" icon={<GlobeIcon size={18} className="text-blue-600 mr-2" data-id="element-4601" />} data-id="element-4600">
          <TabView tabs={[{
          label: 'Map',
          content: <div className="h-96" data-id="element-4603">
                    <GeoPaymentMap data={geoPaymentData} data-id="element-4604" />
                  </div>
        }, {
          label: 'Data',
          content: <GeoPaymentTable data-id="element-4605" />
        }]} data-id="element-4602" />
        </DashboardCard>
      </div>
    </div>;
}